import React, { useContext, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Box, Button, constants, Loading } from '@match/react-component-library';
import {
  useBuildContactUsSC3Request,
  useContactUsEmail,
  useContactUsSelections,
  useContactUsTextArea,
  useDropDownChoices,
  useNameInput,
  useWindowSize,
} from 'modules/hooks';
import api from 'utils/api-services';
import { SiteDataContext } from 'App';
import { DESKTOP_MIN } from 'modules/components/Router/Router';
import BrowserDropdown from './BrowserDropdown';
import CategoryDropdown from './CategoryDropdown';
import DeviceDropdown from './DeviceDropdown';
import EmailAddress from './EmailAddress';
import NameInput from './NameInput';
import OperatingSystemDropdown from './OperatingSystemDropdown';
import QuestionTextArea from './QuestionTextArea';

const { SPACE } = constants;

const emailRegex = /^[^@\s]+@[^\.\s]+\.[^\s]+$/;

const ContactUsSC3: React.FunctionComponent = () => {
  const { isLoggedIn } = useContext(SiteDataContext);
  const [postRequestPending, setPostRequestPending] = useState<boolean>();
  const [contactUsFormCompleted, setContactUsFormCompleted] = useState<boolean>(false);

  const [width] = useWindowSize();

  const { questionText, onQuestionTextChange } = useContactUsTextArea();
  const { emailAddress, emailError, onEmailAdd, onEmailError } = useContactUsEmail();
  const { nameInput, nameInputError, onNameInputAdd, onNameInputError } = useNameInput();

  const {
    browserSelection,
    categorySelection,
    deviceSelection,
    onBrowserSelect,
    onCategorySelect,
    onDeviceSelect,
    onOperatingSystemSelect,
    operatingSystemSelection,
  } = useContactUsSelections();
  const {
    categoryChoices,
    deviceChoices,
    operatingSystemChoices,
    supportedBrowsersChoices,
  } = useDropDownChoices();

  const { requestBody } = useBuildContactUsSC3Request({
    browserSelection,
    categorySelection,
    deviceSelection,
    emailAddress,
    nameInput,
    operatingSystemSelection,
    questionText,
  });

  const handleSubmit = () => {
    if (emailAddress && !emailAddress.trim().match(emailRegex)) {
      onEmailError(true);
    }
    if (!isLoggedIn && (!nameInput || nameInput === '')) {
      onNameInputError(true);
    } else {
      if (nameInputError) {
        onNameInputError(false);
      }
      if (emailError) {
        onEmailError(false);
      }
      setPostRequestPending(true);
      api
        .postQuestionSubmit(requestBody)
        .then(() => {
          setPostRequestPending(false);
          setContactUsFormCompleted(true);
        })
        .catch((error) => new Error(error));
    }
  };

  const pageWidth = width > DESKTOP_MIN ? '50%' : '100%';

  const buttonDisabled =
    questionText.length === 0 ||
    !emailAddress ||
    emailAddress.length === 0 ||
    (!isLoggedIn && (!nameInput || nameInput === ''));

  if (postRequestPending) {
    // TODO Tentative loading state while POST request goes through
    // I just hated the "lag", but this is subject to change
    return (
      <Box>
        <Loading />
      </Box>
    );
  }

  if (contactUsFormCompleted) {
    return (
      <Box>
        <FormattedMessage {...messages.submissionCompleted} />
      </Box>
    );
  }

  return (
    <Box width={pageWidth} paddingX={SPACE.MEDIUM}>
      {!isLoggedIn && (
        <NameInput
          nameInput={nameInput}
          nameInputError={nameInputError}
          onNameInputAdd={onNameInputAdd}
        />
      )}
      <EmailAddress emailAddress={emailAddress} emailError={emailError} onEmailAdd={onEmailAdd} />
      <CategoryDropdown onCategorySelect={onCategorySelect} categoryChoices={categoryChoices} />
      <BrowserDropdown
        onBrowserSelect={onBrowserSelect}
        supportedBrowsers={supportedBrowsersChoices}
      />
      <DeviceDropdown onDeviceSelect={onDeviceSelect} deviceChoices={deviceChoices} />
      <OperatingSystemDropdown
        operatingSystemChoices={operatingSystemChoices}
        onOperatingSystemSelect={onOperatingSystemSelect}
      />
      <QuestionTextArea onQuestionAdd={onQuestionTextChange} questionText={questionText} />
      <Button onClick={handleSubmit} disabled={buttonDisabled}>
        <FormattedMessage {...messages.submitButton} />
      </Button>
    </Box>
  );
};

export default ContactUsSC3;

const messages = defineMessages({
  submitButton: {
    id: 'src/modules/pages/ContactUsPage/ContactUsSC3/ContactUsSC3:submitButton',
    description: 'submit button text',
    defaultMessage: 'Submit',
  },
  submissionCompleted: {
    id: 'src/modules/pages/ContactUsPage/ContactUsSC3/ContactUsSC3:submissionCompleted',
    description: 'form submission completed message',
    defaultMessage: 'Your form has been submitted. Thanks.', // TODO: Add actual something
  },
});
